import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DocumentDuplicateIcon } from '@heroicons/react/outline';
import html2pdf from "html2pdf.js";
import { useReactToPrint } from "react-to-print";

const OptionsBar = ({ htmlContent, dynamicActions, contentRef }) => {

    const pageStyle = `
        @page {
            size: auto;
            margin: 0mm 4mm;
            margin-bottom:20mm;
            @top-left { content: none; }
            @top-center { content: none; }
            @top-right { content: none; }
            @bottom-right {
                content: counter(page) " / " counter(pages);
                font-size: 12px;
            }
        }
        @media print {
            html, body {
                width: auto;
            }
        }
    `;

    // Supprime toutes les div.katex-mathml d'un HTML donné
    const sanitizeHtmlRemoveKatexMathml = (html) => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            const nodes = doc.querySelectorAll('.katex-mathml');
            nodes.forEach(n => n.remove());
            return doc.body.innerHTML;
        } catch (err) {
            // Si parsing échoue, retourner le HTML original
            return html;
        }
    };

    // UI state for processing popup
    const [isProcessing, setIsProcessing] = useState(false);
    const [elapsedSec, setElapsedSec] = useState(0);
    const [estimatedSec, setEstimatedSec] = useState(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const wrapperRef = useRef(null);
    const [popupPos, setPopupPos] = useState(null);

    const startProcessing = (initialEstimateSec = null) => {
        setIsProcessing(true);
        setElapsedSec(0);
        setEstimatedSec(initialEstimateSec);
        startTimeRef.current = Date.now();
        // compute center of the wrapper for popup placement
        try {
            if (wrapperRef.current) {
                const rect = wrapperRef.current.getBoundingClientRect();
                setPopupPos({
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                    centerX: rect.left + rect.width / 2,
                    centerY: rect.top + rect.height / 2
                });
            } else {
                setPopupPos(null);
            }
        } catch (e) {
            setPopupPos(null);
        }
        if (timerRef.current) clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
            setElapsedSec(elapsed);
        }, 1000);
    };

    const stopProcessing = () => {
        setIsProcessing(false);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        startTimeRef.current = null;
        setElapsedSec(0);
        setEstimatedSec(null);
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);


    const handleExportPDFRemote = async () => {
        // Start processing UI
        startProcessing();
        try {
            const endpoint = 'https://files.kojyto.fr/html-to-pdf-weasy';

            // Préparer le payload attendu par l'API en nettoyant le HTML des div.katex-mathml
            const cleanedHtml = sanitizeHtmlRemoveKatexMathml(htmlContent);
            const payload = { html: cleanedHtml };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text().catch(() => null);
                throw new Error(`Remote PDF export failed: ${res.status} ${res.statusText} ${text || ''}`);
            }

            const contentType = res.headers.get('content-type') || '';

            // Si l'API renvoie un JSON comprenant une URL de téléchargement

            const json = await res.json().catch(() => null);
            console.log(json)
                if (json && json.url) {
                    const downloadUrl = json.url.startsWith('http') ? json.url : (json.url.startsWith('/') ? json.url : ('/' + json.url));
                    let filename = 'export-remote.pdf';
                    if (json.path) filename = json.path.split('/').pop() || filename;

                    // Essayer de fetcher le fichier pour forcer le téléchargement (utile si cross-origin est autorisé par CORS)
                    try {
                        const fileRes = await fetch(downloadUrl);
                        if (fileRes.ok) {
                            const fileBlob = await fileRes.blob();
                            // essayer d'obtenir un nom depuis les headers
                            const cd = fileRes.headers.get('content-disposition');
                            if (cd) {
                                const m = /filename\*=UTF-8''(.+)$/.exec(cd) || /filename="?([^";]+)"?/.exec(cd);
                                if (m && m[1]) filename = decodeURIComponent(m[1]);
                            }

                            const blobUrl = window.URL.createObjectURL(fileBlob);
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = filename;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(blobUrl);
                            stopProcessing();
                            console.log('Remote PDF downloaded via fetch from JSON URL');
                            return;
                        } else {
                            // si on ne peut pas récupérer (403/CORS/etc), ouvrir en onglet
                            window.open(downloadUrl, '_blank');
                            stopProcessing();
                            return;
                        }
                    } catch (e) {
                        // probablement une erreur CORS ou réseau : retomber sur l'ouverture dans un onglet
                        window.open(downloadUrl, '_blank');
                        stopProcessing();
                        return;
                    }

                
            }

        
        } catch (err) {
            stopProcessing();
            console.error('Remote PDF export failed', err);
            // Optionnel : afficher notification d'erreur à l'utilisateur
        }
    };

    const handleCopy = async () => {
        try {
            const tempElement = document.createElement('div');
            // Utiliser la version nettoyée du HTML pour éviter d'inclure les div.katex-mathml
            tempElement.innerHTML = sanitizeHtmlRemoveKatexMathml(htmlContent);

            const processNode = (node) => {
                if (node.nodeType === Node.TEXT_NODE) {
                    return node.textContent;
                }
                
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const tag = node.tagName.toLowerCase();

                    // Traitement spécial pour les éléments KaTeX
                    if (node.classList.contains('katex-display') || node.classList.contains('katex')) {
                        const texSource = node.querySelector('.katex-mathml annotation[encoding="application/x-tex"]');
                        if (texSource) {
                            const isBlock = node.classList.contains('katex-display');
                            return isBlock ? `\n$$${texSource.textContent}$$\n` : `$${texSource.textContent}$`;
                        }
                        // Si on ne trouve pas la source LaTeX, on retourne le texte rendu
                        return node.textContent;
                    }

                    let content = '';
                    for (const childNode of node.childNodes) {
                        content += processNode(childNode);
                    }

                    // Ajouter des sauts de ligne pour certains éléments
                    if (['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol'].includes(tag)) {
                        content = `\n${content.trim()}\n`;
                    } else if (tag === 'li') {
                        content = `\n• ${content.trim()}`;
                    } else if (tag === 'br') {
                        content = '\n';
                    }

                    return content;
                }

                return '';
            };

            let plainText = processNode(tempElement);

            // Nettoyer le texte final
            plainText = plainText
                .replace(/\n+/g, '\n')  // Limiter à un seul saut de ligne consécutif
                .replace(/^\s+/gm, '')  // Supprimer les espaces en début de ligne
                .trim();  // Supprimer les espaces au début et à la fin du texte

            await navigator.clipboard.writeText(plainText);
            console.log('Text copied to clipboard');
            // Optionnel : Afficher un message de succès à l'utilisateur
        } catch (err) {
            console.error('Failed to copy text: ', err);
            // Optionnel : Afficher un message d'erreur à l'utilisateur
        }
    };


    const handleExportPDF = () => {
        const element = contentRef.current;
        const opt = {
          margin:       0.5,
          filename:     'markdown-math.pdf',
          image:        { type: 'jpeg', quality: 0.98 },
          html2canvas:  { scale: 1 },
          jsPDF:        { unit: 'in', format: 'a4', orientation: 'portrait' },
          pagebreak:    { mode: ['avoid-all', 'css', 'legacy'] } 
        };
        html2pdf().set(opt).from(element).save();
    };
    
    const reactToPrintFn = useReactToPrint({ pageStyle, contentRef});

    return (
        <>
            <div ref={wrapperRef} className="relative flex items-center space-x-2 p-2 bg-gray-100 rounded-md">
                <button 
                    onClick={handleCopy}
                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
                >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span className="text-sm">Copy</span>
                </button>

                <button 
                    onClick={handleExportPDFRemote}
                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
                >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span className="text-sm">Export pdf</span>
                </button>

                <button 
                    onClick={reactToPrintFn}
                    className="flex items-center space-x-1 text-gray-600 hover:text-gray-800"
                >
                    <DocumentDuplicateIcon className="w-4 h-4" />
                    <span className="text-sm">Imprimer</span>
                </button>

                {dynamicActions.map((element, index) => (
                    element.cond && (
                        <button 
                            key={index}
                            onClick={(e) => {e.preventDefault(); element.action()}} 
                            className={"flex items-center space-x-1 text-gray-600 hover:text-gray-800 " + element.color}
                        > 
                            <DocumentDuplicateIcon className="w-4 h-4" />
                            <span className="text-sm">{element.name}</span>
                        </button>
                    )
                ))}
            
            </div>

        {isProcessing && createPortal(
            (popupPos ? (
                <div style={{position: 'fixed', top: popupPos.top + 'px', left: popupPos.left + 'px', width: popupPos.width + 'px', height: popupPos.height + 'px', zIndex: 2147483647}}>
                    <div style={{position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)'}} />
                    <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', minWidth: 240}}>
                        <div style={{fontWeight: 600, marginBottom: 8}}>PDF en cours de traitement</div>
                        <div style={{fontSize: 13, marginBottom: 6}}>Temps écoulé: {elapsedSec}s</div>
                        <div style={{fontSize: 13}}>Estimation: {estimatedSec !== null ? `${estimatedSec}s` : 'N/A'}</div>
                    </div>
                </div>
            ) : (
                // fallback: center of viewport
                <div style={{position: 'fixed', inset: 0, zIndex: 2147483647, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <div style={{position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)'}} />
                    <div style={{position: 'relative', background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', minWidth: 240}}>
                        <div style={{fontWeight: 600, marginBottom: 8}}>PDF en cours de traitement</div>
                        <div style={{fontSize: 13, marginBottom: 6}}>Temps écoulé: {elapsedSec}s</div>
                        <div style={{fontSize: 13}}>Estimation: {estimatedSec !== null ? `${estimatedSec}s` : 'N/A'}</div>
                    </div>
                </div>
            )), document.body)
        }
        </>
    );
};

export default OptionsBar;

//faire faire de la géométrie avec tikz ?